import React, { useState, useEffect } from 'react';
import { Input, Button, Alert } from '../common';
import { Building2, MapPin, Phone, Mail } from 'lucide-react';

const OrganizacionForm = ({
  organizacion = null,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
}) => {
  const isEdit = !!organizacion;

  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'empresa',
    sector: '',
    tamaño: '',
    pais: 'Colombia',
    telefono: '',
    email_contacto: '',
    activo: true,
  });

  const [formErrors, setFormErrors] = useState({});

  // Cargar datos si es edición
  useEffect(() => {
    if (organizacion) {
      setFormData({
        nombre: organizacion.nombre || '',
        tipo: organizacion.tipo || 'empresa',
        sector: organizacion.sector || '',
        tamaño: organizacion.tamaño || '',
        pais: organizacion.pais || 'Colombia',
        telefono: organizacion.telefono || '',
        email_contacto: organizacion.email_contacto || '',
        activo: organizacion.activo !== false,
      });
    }
  }, [organizacion]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    // Limpiar error del campo
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    }

    if (formData.email_contacto && formData.email_contacto.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email_contacto)) {
        errors.email_contacto = 'Email inválido';
      }
    }

    if (formData.telefono && formData.telefono.trim()) {
      const phoneRegex = /^[0-9\s\-\+\(\)]+$/;
      if (!phoneRegex.test(formData.telefono)) {
        errors.telefono = 'Teléfono inválido';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Preparar datos - enviar solo campos no vacíos
    const submitData = {
      nombre: formData.nombre,
      tipo: formData.tipo,
      activo: formData.activo,
    };

    if (formData.sector.trim()) submitData.sector = formData.sector;
    if (formData.tamaño) submitData.tamaño = formData.tamaño;
    if (formData.pais.trim()) submitData.pais = formData.pais;
    if (formData.telefono.trim()) submitData.telefono = formData.telefono;
    if (formData.email_contacto.trim()) submitData.email_contacto = formData.email_contacto;

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert type="error" message={error} />}

      <Input
        label="Nombre de la Organización"
        name="nombre"
        type="text"
        placeholder="Ej: Universidad de Caldas"
        value={formData.nombre}
        onChange={handleChange}
        error={formErrors.nombre}
        icon={Building2}
        required
        disabled={loading}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Organización *
          </label>
          <select
            name="tipo"
            value={formData.tipo}
            onChange={handleChange}
            disabled={loading}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          >
            <option value="empresa">Empresa</option>
            <option value="organizacion">Organización</option>
            <option value="comunidad">Comunidad</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tamaño
          </label>
          <select
            name="tamaño"
            value={formData.tamaño}
            onChange={handleChange}
            disabled={loading}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          >
            <option value="">Seleccionar...</option>
            <option value="pequeña">Pequeña</option>
            <option value="mediana">Mediana</option>
            <option value="grande">Grande</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Sector"
          name="sector"
          type="text"
          placeholder="Ej: Educación, Tecnología"
          value={formData.sector}
          onChange={handleChange}
          disabled={loading}
        />

        <Input
          label="País"
          name="pais"
          type="text"
          placeholder="Colombia"
          value={formData.pais}
          onChange={handleChange}
          disabled={loading}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Teléfono"
          name="telefono"
          type="text"
          placeholder="Ej: +57 300 123 4567"
          value={formData.telefono}
          onChange={handleChange}
          error={formErrors.telefono}
          icon={Phone}
          disabled={loading}
        />

        <Input
          label="Email de Contacto"
          name="email_contacto"
          type="email"
          placeholder="contacto@organizacion.com"
          value={formData.email_contacto}
          onChange={handleChange}
          error={formErrors.email_contacto}
          icon={Mail}
          disabled={loading}
        />
      </div>

      <div className="flex items-center">
        <input
          id="activo"
          name="activo"
          type="checkbox"
          checked={formData.activo}
          onChange={handleChange}
          disabled={loading}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="activo" className="ml-2 block text-sm text-gray-900">
          Organización activa
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" loading={loading}>
          {isEdit ? 'Actualizar Organización' : 'Crear Organización'}
        </Button>
      </div>
    </form>
  );
};

export default OrganizacionForm;
